<template>
  <div class="modal fade" tabindex="-1" id="TissBatchModal"
       data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
          <div class="modal-header">
          <h5 class="modal-title">Criar Lote de Guias</h5>
          <div class="btn btn-icon btn-sm btn-active-light-primary ms-2"
               @click="closeModal()"
               data-bs-dismiss="modal" aria-label="Fechar">
            <i class="bi bi-x-lg"></i>
          </div>
        </div>

        <div class="modal-body">
          <div class="mb-5">
            <label class="fs-6 fw-bold mb-2 required">Tipo de Lote</label>
            <input
              v-if="isBatchTypeLocked"
              type="text"
              class="form-control form-control-solid"
              :value="batchTypeLabel(form.batch_type)"
              readonly
              disabled
            >
            <select v-else
                    v-model="form.batch_type"
                    class="form-control form-control-solid"
                    required>
              <option value="">Selecione o tipo de lote</option>
              <option value="tiss">Recebimento (TISS)</option>
              <option value="payment">Pagamento</option>
            </select>
            <small class="text-muted">
              <template v-if="isBatchTypeLocked">
                Tipo definido pelo filtro <strong>Disponíveis para lote</strong> na listagem de guias.
              </template>
              <template v-else>
                <strong>Lote de recebimento (TISS):</strong> Para enviar guias aos convênios via TISS e gerar contas a receber.<br>
                <strong>Lote de pagamento:</strong> Para gerar contas a pagar aos profissionais.
              </template>
            </small>
          </div>

          <div class="mb-5">
            <label class="fs-6 fw-bold mb-2">Observações</label>
            <textarea v-model="form.notes"
                      class="form-control form-control-solid"
                      rows="3"
                      maxlength="1000"
                      placeholder="Observações sobre o lote (opcional)"></textarea>
            <small class="text-muted">{{ (form.notes || '').length }}/1000 caracteres</small>
          </div>

          <div class="mb-5">
            <h6 class="fw-bold mb-3">Resumo do Lote</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Total de Guias</th>
                    <th>Valor Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>{{ selectedGuides.length }}</strong></td>
                    <td><strong>R$ {{ formatCurrency(totalAmount) }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="selectedGuides.length > 0" class="mb-5">
            <h6 class="fw-bold mb-3">Guias Selecionadas ({{ selectedGuides.length }})</h6>
            <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
              <table class="table table-sm table-bordered">
                <thead class="table-light sticky-top">
                  <tr>
                    <th>Número</th>
                    <th>Paciente</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                  <tbody>
                    <tr v-for="guideId in selectedGuides" :key="guideId">
                      <td>{{ getGuideInfo(guideId)?.guide_number || getGuideInfo(guideId)?.guide?.guide_number || guideId }}</td>
                      <td>{{ getGuideInfo(guideId)?.patient_name || getGuideInfo(guideId)?.guide?.patient_name || '-' }}</td>
                      <td>R$ {{ formatCurrency(guideAmount(getGuideInfo(guideId))) }}</td>
                    </tr>
                  </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal()">Cancelar</button>
          <button type="button" class="btn btn-primary" :disabled="loading || !form.batch_type" @click="submitForm">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            Criar Lote
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";
import Modal from "@/helpers/modal";
import Loader from "@/helpers/loader";

export default {
  name: "TissBatchModal",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    selectedGuides: {
      type: Array,
      default: () => [],
    },
    initialBatchType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      guidesInfo: [],
      form: {
        batch_type: '',
        notes: '',
      },
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    isBatchTypeLocked() {
      return ['tiss', 'payment'].includes(this.initialBatchType);
    },
    totalAmount() {
      return this.guidesInfo.reduce((sum, guide) => sum + this.guideAmount(guide), 0);
    },
  },
  watch: {
    show(newValue) {
      if (newValue) {
        if (this.isBatchTypeLocked) {
          this.form.batch_type = this.initialBatchType;
        }
        this.loadGuidesInfo();
        setTimeout(() => {
          Modal.toggle('#TissBatchModal');
        }, 100);
      }
    },
    initialBatchType(value) {
      if (this.isBatchTypeLocked) {
        this.form.batch_type = value;
      }
    },
  },
  methods: {
    batchTypeLabel(type) {
      if (type === 'tiss') return 'Recebimento (TISS)';
      if (type === 'payment') return 'Pagamento';
      return '-';
    },
    loadGuidesInfo() {
      if (!this.place?.id_place || this.selectedGuides.length === 0) {
        this.guidesInfo = [];
        return;
      }

      // Buscar informações das guias selecionadas individualmente
      this.loadGuidesIndividually();
    },
    loadGuidesIndividually() {
      this.guidesInfo = [];
      const promises = this.selectedGuides.map(guideId => 
        http.get(`/places/${this.place.id_place}/tiss-guides/${guideId}`)
          .then(response => response.data)
          .catch(() => null)
      );

      Promise.all(promises).then(results => {
        this.guidesInfo = results.filter(r => r !== null);
      });
    },
    getGuideInfo(guideId) {
      const guide = this.guidesInfo.find(g => 
        g.id === guideId || 
        g.id_tiss_guide === guideId ||
        (g.guide && (g.guide.id === guideId || g.guide.id_tiss_guide === guideId))
      );
      return guide;
    },
    guideAmount(guide) {
      if (!guide) return 0;
      const g = guide.guide || guide;
      if (g.amount != null && g.amount !== '') {
        return parseFloat(g.amount) || 0;
      }
      if (this.form.batch_type === 'payment') {
        return parseFloat(g.procedure_transfer_value ?? 0) || 0;
      }
      return parseFloat(g.procedure_value ?? 0) || 0;
    },
    submitForm() {
      if (this.isBatchTypeLocked) {
        this.form.batch_type = this.initialBatchType;
      }

      if (!this.form.batch_type) {
        Alert.error('Selecione o tipo de lote');
        return;
      }

      if (this.selectedGuides.length === 0) {
        Alert.error('Selecione pelo menos uma guia');
        return;
      }

      this.loading = true;
      Loader.show('Criando lote...');

      const data = {
        guide_ids: this.selectedGuides.map(id => Number(id)),
        batch_type: this.isBatchTypeLocked ? this.initialBatchType : this.form.batch_type,
        notes: (this.form.notes || '').trim().slice(0, 1000) || null,
      };

      http.post(`/places/${this.place.id_place}/tiss-batches`, data)
        .then((response) => {
          const res = response.data?.data ?? response.data;
          Alert.success(res ? 'Lote criado com sucesso!' : 'Lote criado com sucesso!');
          this.$emit('saved', res);
          this.closeModal();
          this.$router.push('/tiss/batches');
        })
        .catch((error) => {
          const dataErr = error.response?.data;
          if (error.response?.status === 422) {
            const msg = dataErr?.message || dataErr?.error || (dataErr?.errors && Object.values(dataErr.errors).flat().length ? Object.values(dataErr.errors).flat()[0] : 'Dados inválidos');
            Alert.error(msg);
            return;
          }
          if (error.response?.status === 403 && dataErr?.error) {
            Alert.error(dataErr.error);
            return;
          }
          Alert.error(dataErr?.error || dataErr?.message || 'Erro ao criar lote');
          console.error(error);
        })
        .finally(() => {
          this.loading = false;
          Loader.close();
        });
    },
    closeModal() {
      Modal.toggle('#TissBatchModal');
      this.form = {
        batch_type: this.initialBatchType && ['tiss', 'payment'].includes(this.initialBatchType) ? this.initialBatchType : '',
        notes: '',
      };
      this.$emit('close');
    },
    formatCurrency(value) {
      return parseFloat(value || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
  },
};
</script>

<style scoped>
.required::after {
  content: " *";
  color: red;
}
</style>

